import { Component, OnInit, OnDestroy, inject, Injector, ChangeDetectionStrategy, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { NavigationEnd, Router } from '@angular/router';
import { LoadingService } from '../../../core/services/loading.service';
import { Subscription, filter } from 'rxjs';
import { NetworkGuardService } from '../../../core/services/network-guard.service';
import { DialogService } from '../../../core/services/dialog.service';

/** DISC-012: how often an open tab asks the service worker for a new version, and the shortest gap between two asks. */
const UPDATE_CHECK_EVERY_MS = 30 * 60_000;
const UPDATE_CHECK_MIN_GAP_MS = 60_000;

@Component({
    selector: 'app-pwa-update',
    imports: [CommonModule],
    template: `
    <div class="update-banner" *ngIf="showUpdate">
      <span>A new version of MediTrack is available.</span>
      <button (click)="update()">Update Now</button>
    </div>
  `,
    changeDetection: ChangeDetectionStrategy.Eager,
    styles: [`
    .update-banner {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      background: #1E3F99;
      color: white;
      padding: 10px 20px;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 16px;
      z-index: 10001;
      font-size: 13px;
      font-weight: 500;
      animation: slideDown 0.3s ease-out;
    }
    @keyframes slideDown {
      from { transform: translateY(-100%); }
      to { transform: translateY(0); }
    }
    button {
      background: white;
      color: #1E3F99;
      border: none;
      border-radius: 6px;
      padding: 6px 16px;
      font-size: 12px;
      font-weight: 700;
      cursor: pointer;
      font-family: inherit;
    }
  `]
})
export class PwaUpdateComponent implements OnInit, OnDestroy {
  showUpdate = false;
  private sub?: Subscription;
  private navSub: Subscription | null = null;
  private swUpdate: SwUpdate | null = null;
  private injector = inject(Injector);
  private networkGuard = inject(NetworkGuardService);
  private dialog = inject(DialogService);
  private readonly zone = inject(NgZone);
  private checkTimer: ReturnType<typeof setInterval> | null = null;
  private lastCheckAt = 0;
  private readonly onWake = (): void => this.checkForUpdateSoon();
  private loading = inject(LoadingService);
  private router = inject(Router, { optional: true });
  /** TC-4529 (2026-11-03): typed on this screen since it opened. Update Now reloads the page, which throws that away. */
  private typedOnThisScreen = false;
  private readonly onTyped = (ev: Event): void => {
    const el = ev.target as HTMLElement | null;
    if (el && /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName)) this.typedOnThisScreen = true;
  };

  ngOnInit(): void {
    // SwUpdate is only available when service worker is enabled (production)
    try {
      this.swUpdate = this.injector.get(SwUpdate);
    } catch {
      return; // Service worker not available (dev mode)
    }

    if (!this.swUpdate?.isEnabled) return;

    this.navSub = this.router?.events.pipe(filter(e => e instanceof NavigationEnd)).subscribe(() => { this.typedOnThisScreen = false; }) ?? null;

    this.sub = this.swUpdate.versionUpdates.pipe(
      filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY')
    ).subscribe(() => {
      this.zone.run(() => { this.showUpdate = true; });
    });

    this.zone.runOutsideAngular(() => {
      this.checkTimer = setInterval(() => this.checkForUpdateSoon(), UPDATE_CHECK_EVERY_MS);
      document.addEventListener('input', this.onTyped, true);
      window.addEventListener('online', this.onWake);
      document.addEventListener('visibilitychange', this.onWake);
    });
  }

  /** DISC-012: API calls carry 'ngsw-bypass', so they no longer start the worker's own update check. Ask it ourselves. */
  private checkForUpdateSoon(): void {
    const sw = this.swUpdate;
    if (!sw?.isEnabled) return;
    try { if (document.visibilityState !== 'visible' || navigator.onLine === false) return; } catch { return; }
    const now = Date.now();
    if (now - this.lastCheckAt < UPDATE_CHECK_MIN_GAP_MS) return;
    this.lastCheckAt = now;
    try { sw.checkForUpdate().catch(() => {}); } catch {}
  }

  async update(): Promise<void> {
    // TC-4529: a payment on its way would be cut off, leaving the cashier unsure whether it went through.
    if (this.loading.writesInFlight > 0) {
      this.dialog.alert('A save is still on its way to the server. Wait a moment, then press Update Now again.', { tone: 'warning' });
      return;
    }
    if (this.typedOnThisScreen &&
        !(await this.dialog.confirm('You have typed on this screen. Updating reloads the page, and anything not saved yet will be lost. Update anyway?',
          { tone: 'warning', title: 'Update and reload MediTrack?', okText: 'Update now', cancelText: 'Not now' }))) {
      return;
    }

    // LOW-7 (audit 2026-06-16): the reload aborts an in-flight "Send All" of the offline write-queue
    // and tears down the retry panel. The queue persists in localStorage so nothing is LOST, but
    // confirm first when there are unsynced items so the disruption isn't a surprise.
    let pending = 0;
    try { pending = this.networkGuard.pendingCount; } catch {}
    if (pending > 0 &&
        !(await this.dialog.confirm(`You have ${pending} unsynced change(s) queued offline. Updating reloads the app — they'll sync once it reopens. Continue?`,
          { tone: 'warning', title: 'Update and reload MediTrack?', okText: 'Update now', cancelText: 'Not now' }))) {
      return;
    }

    // AUDIT FIX (2026-06-16): a bare reload does NOT reliably switch an Angular ngsw client to the
    // newly-installed VERSION_READY build — the existing service worker keeps serving the cached
    // old shell, so "Update Now" could reload into the SAME version and re-show the banner. Activate
    // the waiting version first, THEN reload. Fall back to a plain reload if activation isn't
    // available/fails so the button is never a no-op.
    if (this.swUpdate?.isEnabled) {
      this.swUpdate.activateUpdate()
        .then(() => window.location.reload())
        .catch(() => window.location.reload());
    } else {
      window.location.reload();
    }
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    if (this.checkTimer) clearInterval(this.checkTimer);
    this.checkTimer = null;
    this.navSub?.unsubscribe();
    document.removeEventListener('input', this.onTyped, true);
    window.removeEventListener('online', this.onWake);
    document.removeEventListener('visibilitychange', this.onWake);
  }
}
